'use client'

import type React from 'react'
import { forwardRef, type RefAttributes } from 'react'

import {
  getFlexChildClassName,
  getFlexParentClassName,
  type ThemeFlexChild,
  type ThemeFlexParent,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'

import { type UseSizingProps, useSizing } from '../../hooks/use-sizing.ts'
import { type UseSpacingProps, useSpacing } from '../../hooks/use-spacing.ts'
import { getTestProps, type TestIds } from '../../utils/testing.ts'

type StackProps = Readonly<
  React.PropsWithChildren<
    RefAttributes<HTMLElement> &
      ThemeFlexParent &
      ThemeFlexChild &
      UseSizingProps &
      UseSpacingProps &
      TestIds & {
        as?: React.ElementType
        className?: string
        onClick?: () => void
      }
  >
>

const Stack = forwardRef<HTMLElement, StackProps>(
  (
    {
      as: Component = 'div',
      children,
      className,
      dataTestId,
      dataTrackingId,
      onClick,
      ...props
    }: StackProps,
    ref
  ) => {
    const { widthClassName, heightClassName } = useSizing(props)
    const { marginClassName, paddingClassName } = useSpacing(props)

    return (
      <Component
        className={cn(
          getFlexParentClassName(props),
          getFlexChildClassName(props),
          widthClassName,
          heightClassName,
          marginClassName,
          paddingClassName,
          className
        )}
        onClick={onClick}
        ref={ref}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        {children}
      </Component>
    )
  }
)

export { Stack, type StackProps }
